// Email + password sign-in for invited users (Supabase Email provider). No self-service signup.
import { useState } from "react";
import { formatAuthPasswordError } from "@/lib/auth/formatAuthPasswordError";
import { sendPasswordResetToEmail } from "@/lib/auth/passwordResetEmail";
import { getEmailAuthRedirectTo } from "@/lib/authRedirect";
import { requireSupabase } from "@/lib/supabase";
import { PasswordFieldWithToggle } from "./PasswordFieldWithToggle";

function hasAuthCodeInUrl(): boolean {
  if (typeof window === "undefined") return false;
  return new URL(window.location.href).searchParams.has("code");
}

export function EmailSignInPanel() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showEmailLink, setShowEmailLink] = useState(false);

  async function signInWithPassword() {
    setError(null);
    setInfo(null);
    const trimmed = email.trim().toLowerCase();
    if (!trimmed || !password) {
      setError("Enter your email and password.");
      return;
    }
    setBusy(true);
    try {
      const sb = requireSupabase();
      const { error: pwErr } = await sb.auth.signInWithPassword({ email: trimmed, password });
      if (pwErr) {
        setError(formatAuthPasswordError(pwErr.message));
        return;
      }
      setPassword("");
    } catch (e) {
      setError(formatAuthPasswordError(e instanceof Error ? e.message : ""));
    } finally {
      setBusy(false);
    }
  }

  async function sendMagicLink() {
    setError(null);
    setInfo(null);
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError("Enter your email first.");
      return;
    }
    setBusy(true);
    try {
      const sb = requireSupabase();
      const { error: otpErr } = await sb.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: getEmailAuthRedirectTo(),
          shouldCreateUser: false,
        },
      });
      if (otpErr) {
        setError(otpErr.message);
        return;
      }
      setInfo("If that email is on file, you will receive a sign-in link shortly.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send email.");
    } finally {
      setBusy(false);
    }
  }

  async function forgotPassword() {
    setError(null);
    setInfo(null);
    const r = await sendPasswordResetToEmail(email);
    if (!r.ok) {
      setError(r.message);
      return;
    }
    setInfo("If that email is on file, you will receive a link to set a new password.");
  }

  const codeStillInUrl = hasAuthCodeInUrl();

  return (
    <div className="w-full max-w-md mx-auto rounded-2xl border border-slate-200/90 bg-white/90 dark:border-slate-700/90 dark:bg-slate-900/55 px-4 py-4 text-left shadow-sm backdrop-blur-sm space-y-3">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Work email</p>

      {codeStillInUrl ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          This page could not finish signing you in from the link (often the in-app browser in Mail/Outlook).{" "}
          <span className="font-medium">Copy the link and open it in Safari or Chrome</span>, or sign in below.
        </div>
      ) : null}

      <label className="block">
        <span className="sr-only">Email</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none ring-indigo-500/30 focus:ring-2 dark:border-slate-600 dark:bg-slate-950 dark:text-slate-100"
          placeholder="you@company.com"
          disabled={busy}
        />
      </label>

      <PasswordFieldWithToggle
        label="Password"
        autoComplete="current-password"
        value={password}
        onChange={setPassword}
        disabled={busy}
        placeholder="Password"
        onEnter={() => void signInWithPassword()}
      />

      <div className="text-right -mt-1">
        <button
          type="button"
          disabled={busy}
          onClick={() => void forgotPassword()}
          className="text-xs text-indigo-600 hover:text-indigo-800 underline underline-offset-2 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50"
        >
          Forgot password?
        </button>
      </div>

      <button
        type="button"
        disabled={busy}
        onClick={() => void signInWithPassword()}
        className="w-full rounded-xl bg-indigo-600 px-3 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>

      {error ? <p className="text-xs text-rose-600 dark:text-rose-400">{error}</p> : null}
      {info ? <p className="text-xs text-emerald-700 dark:text-emerald-400">{info}</p> : null}

      {!showEmailLink ? (
        <p className="text-center text-[11px] text-slate-500 dark:text-slate-500 pt-1">
          <button
            type="button"
            disabled={busy}
            onClick={() => setShowEmailLink(true)}
            className="text-indigo-600 hover:text-indigo-800 underline underline-offset-2 dark:text-indigo-400 disabled:opacity-50"
          >
            Trouble signing in? Request an email link
          </button>
        </p>
      ) : (
        <div className="rounded-xl border border-slate-100 bg-slate-50/80 px-3 py-2 dark:border-slate-800 dark:bg-slate-950/50 space-y-2">
          <p className="text-[11px] text-slate-600 dark:text-slate-400">
            We will email a one-time sign-in link to the address above (must already be invited).
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={() => void sendMagicLink()}
            className="w-full rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-900 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-100"
          >
            {busy ? "Sending…" : "Email me a sign-in link"}
          </button>
        </div>
      )}
    </div>
  );
}
